import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { StyledButton } from 'common/components/button/StyledButton';
import { Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { TableHeaderCell } from 'semantic-ui-react';
import { BodyText, PageSection, Subtitle } from 'common/styles/page';
import { assessmentPeriodApi } from '../api/assessmentPeriodApi';
import { useToasts } from 'common/toast';
import { Card } from 'common/components/Card';
import { ASSESSMENT_PERIOD_CLOSED } from '../constants';
import DownloadCSVButton from './AssessmentDownloadCSVButton';
import { AppConfig } from '../../../Config';
import { authenticatedFetch } from '../../../common/utils/authFetch';
import { getCurrentOrganizationId } from '../../../common/selectors/auth';
import {
  useAggregateVersionHealthQuery,
  AggregateVersionEntry,
  AggregateVersionStatus,
} from '../api/aggregateVersionsApi';

const aggregates = [
  { name: 'ALL_AGGREGATES', number: 0, description: 'All aggregate data' },
  { name: 'PILLAR_SCORES', number: 1, description: 'Organization pillar metrics' },
  { name: 'SENTIMENT_SCORES', number: 3, description: 'Sentiment analysis scores' },
  { name: 'MEMBER_PILLARS', number: 6, description: 'Pillar scores for each member / employee' },
  { name: 'TEAM_PILLAR_METRICS', number: 31, description: 'Pillar metrics for each team' },
  {
    name: 'CATEGORY_PILLARS',
    number: 8,
    description: 'Pillar scores for each demographic category',
  },
  { name: 'OUTCOME_SCORES', number: 9, description: 'Overall outcome scores' },
  { name: 'TEAM_OUTCOME_METRICS', number: 30, description: 'Outcome metrics for each team' },
  {
    name: 'CATEGORY_OVERALL',
    number: 10,
    description: 'Overall scores for each demographic category',
  },
  {
    name: 'MEMBER_QUESTION_SCORES',
    number: 12,
    description: 'Anonymized question responses for all members',
  },
  { name: 'OPTION_QUESTION_SCORES', number: 13, description: 'Scores by question with deviation' },
  { name: 'CATEGORY_QUESTION_SCORES', number: 14, description: 'Scores per category question' },

  { name: 'OPTION_OUTCOME_SCORES', number: 24, description: 'Outcome scores by question with deviation' },

  { name: 'MEMBER_FEEDBACK', number: 15, description: 'Feedback provided by members' },
  { name: 'OUTCOME_PREDICTIONS', number: 18, description: 'Outcome correlations with pillars' },
  { name: 'CARTESIAN_AVERAGES', number: 19, description: 'Two-factor category overall scores' },
  {
    name: 'LOOKUP_PROPERTIES',
    number: 20,
    description: 'Various values used for selection lists and filters',
  },
  {
    name: 'SURVEY_COMPLETION_STATISTICS',
    number: 23,
    description: 'Survey data on completed, incomplete, partially-complete, etc.',
  },
  {
    name: 'AI_EMBEDDINGS',
    number: 17,
    description: 'AI-generated payload generation and embeddings',
  },
  {
    name: 'AI_GREATEST_STRENGTHS',
    number: 4,
    description: 'AI-generated greatest strength summaries',
  },
  {
    name: 'AI_GREATEST_OPPORTUNITIES',
    number: 5,
    description: 'AI-generated greatest opportunity summaries',
  },
  {
    name: 'AI_SUMMARY_ORGANIZATION',
    number: 16,
    description: 'AI-generated overall organization summary',
  },
  {
    name: 'THEMES',
    number: 26,
    description: 'AI-generated overall theme summary',
  },
  {
    name: 'KEYWORDS',
    number: 27,
    description: 'AI-generated overall keyword summary',
  },
  {
    name: 'TEAM_SUMMARY',
    number: 28,
    description: 'AI-generated overall team summary v2',
  },
  {
    name: 'SENTIMENT_DISTRIBUTION',
    number: 29,
    description: 'AI-generated distribution of sentiment scores for the teams and organization',
  },
];

const STATUS_DISPLAY: Record<AggregateVersionStatus, { label: string; color: string }> = {
  current: { label: 'Current', color: '#2e7d32' },
  patch_behind: { label: 'Update available (optional)', color: '#0277bd' },
  minor_behind: { label: 'Auto-update pending (minor)', color: '#ef6c00' },
  major_behind: { label: 'Auto-update pending (major)', color: '#c62828' },
  missing: { label: 'Not computed', color: '#757575' },
};

const VersionStatus: React.FC<{ entry?: AggregateVersionEntry }> = ({ entry }) => {
  if (!entry) return <span>—</span>;
  const display = STATUS_DISPLAY[entry.status];
  const versionText =
    entry.status === 'current'
      ? `v${entry.current_version}`
      : entry.status === 'missing'
        ? `v${entry.current_version} available`
        : `v${entry.computed_version ?? '1.0.0'} → v${entry.current_version}`;
  return (
    <span>
      <span style={{ color: display.color, fontWeight: 600 }}>{display.label}</span>
      <br />
      <span style={{ fontSize: 12, color: '#757575' }}>{versionText}</span>
    </span>
  );
};

type AssessmentAdminProps = {
  data: any;
  assessmentId: number;
  onSetSelectedAssessment: (value: number) => void;
  selectedAssessmentPeriod: any;
};

const AssessmentAdmin: React.FC<AssessmentAdminProps> = ({
  assessmentId,
  onSetSelectedAssessment,
  selectedAssessmentPeriod,
}) => {
  const [triggerAssessmentPeriodClose, { isLoading: isClosing }] =
    assessmentPeriodApi.useApiPortalV1AssessmentsCloseCreateMutation();
  const [isDeletingSnapshots, setIsDeletingSnapshots] = useState(false);
  const organizationId = useSelector(getCurrentOrganizationId);

  const { addToast } = useToasts();

  const { data: versionHealth, refetch: refetchVersionHealth } = useAggregateVersionHealthQuery(
    { assessmentPeriodId: selectedAssessmentPeriod?.id },
    { skip: !selectedAssessmentPeriod?.id }
  );
  const healthByType = React.useMemo(() => {
    const map = new Map<number, AggregateVersionEntry>();
    for (const entry of versionHealth?.aggregates ?? []) {
      map.set(entry.aggregate_type, entry);
    }
    return map;
  }, [versionHealth]);

  async function handlePress(
    assessment_period_id: number,
    aggregate_id: number,
    overwrite: boolean = true
  ) {
    try {
      await triggerAssessmentPeriodClose({
        closeAssessmentPeriod: {
          assessment_period_id: assessment_period_id,
          overwrite: true,
          aggregate_id: aggregate_id,
        },
      }).unwrap();

      addToast(`Successfully created aggregates`, {
        appearance: 'success',
        autoDismiss: true,
      });
      refetchVersionHealth();
    } catch (error) {
      addToast(`Failed to create aggregates`, {
        appearance: 'error',
        autoDismiss: true,
      });

      console.error('Error:', error);
    }
  }

  async function handleDeleteSnapshots() {
    if (!selectedAssessmentPeriod?.id || !organizationId || isDeletingSnapshots) {
      return;
    }

    const confirmed = window.confirm(
      'Delete snapshots for this assessment period? This cannot be undone.'
    );
    if (!confirmed) {
      return;
    }

    setIsDeletingSnapshots(true);

    try {
      const url = `${AppConfig.api.apiUrl}/api/portal/v1/team-snapshots/delete-snapshots/?assessment_period_id=${selectedAssessmentPeriod.id}`;
      const response = await authenticatedFetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'X-NULOGIC-ORGANIZATION-ID': organizationId.toString(),
        },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          `Failed to delete team snapshots: ${response.status} ${response.statusText} ${body?.detail ?? ''}`
        );
      }

      addToast(`Team snapshots deleted successfully`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error('Failed to delete team snapshots:', error);
      addToast('Failed to delete team snapshots', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDeletingSnapshots(false);
    }
  }

  return (
    <PageSection padbottom data-testid="admin-assessments-page">
      <Card data-testid="admin-assessments-header">
        <PageSection
          gap="15px"
          padbottom="10px"
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          width="100%"
        >
          <PageSection
            flex="12"
            justifyContent="flex-start"
            alignItems="center"
            flexDirection="row"
            display="flex"
          >
            <Subtitle>Assessment: {selectedAssessmentPeriod?.name}</Subtitle>
          </PageSection>
          <StyledButton
            data-testid="admin-assessments-close"
            onClick={() => {
              if (window.confirm('Are you sure you want to close this assessment period?')) {
                handlePress(selectedAssessmentPeriod.id, 0, false);
              }
            }}
            disabled={
              !selectedAssessmentPeriod ||
              selectedAssessmentPeriod?.status === ASSESSMENT_PERIOD_CLOSED ||
              isClosing
            }
            label={isClosing ? 'Closing...' : 'Close Assessment'}
            color="green"
          />

          <DownloadCSVButton
            data-testid="admin-assessments-download"
            assessmentPeriodId={selectedAssessmentPeriod?.id}
            disabled={
              !selectedAssessmentPeriod ||
              selectedAssessmentPeriod?.status !== ASSESSMENT_PERIOD_CLOSED
            }
            filename="survey_export.csv"
          />
        </PageSection>

        <PageSection
          gap="15px"
          padbottom="10px"
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          width="100%"
        >
          <BodyText>
            Closing an assessment period will prevent any further survey responses from being
            submitted and runs processes to generate aggregate data for analyitics and AI summaries.
            This process takes about five minutes to complete largely due to the time taken by AI
            processing.
            <br />
            <br />
            In the table below, aggregates can be regenerated for closed assessment periods to
            update aggregate schemas or recreate AI inconsistencies.
            <br />
            <br />
            Every stored aggregate is stamped with the calculation version that produced it.
            Major and minor calculation updates are applied automatically by background workers
            and show here as pending until they complete; patch updates are optional and can be
            applied manually with Generate.
          </BodyText>
        </PageSection>
      </Card>

      <PageSection padtop="30px">
        <Subtitle>Regenerate Aggregate Data</Subtitle>
      </PageSection>
      <Table data-testid="admin-assessments-table">
        <TableHeader>
          <TableRow>
            <TableHeaderCell width={4}>Aggregate</TableHeaderCell>
            <TableHeaderCell width={1}>Number</TableHeaderCell>
            <TableHeaderCell width={6}>Description</TableHeaderCell>
            <TableHeaderCell width={3}>Version Status</TableHeaderCell>
            <TableHeaderCell width={2}>Action</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {aggregates.map(aggregate => (
            <TableRow key={aggregate.number}>
              <TableCell $firstcol>{aggregate.name}</TableCell>
              <TableCell>{aggregate.number}</TableCell>
              <TableCell>{aggregate.description}</TableCell>
              <TableCell>
                {aggregate.number === 0 ? '—' : <VersionStatus entry={healthByType.get(aggregate.number)} />}
              </TableCell>
              <TableCell>
                <StyledButton
                  onClick={() => handlePress(selectedAssessmentPeriod.id, aggregate.number)}
                  disabled={selectedAssessmentPeriod?.status !== ASSESSMENT_PERIOD_CLOSED || isClosing}
                  label={isClosing ? 'Working...' : 'Generate'}
                  color="blue"
                />
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell $firstcol>Delete team snapshot</TableCell>
            <TableCell>-</TableCell>
            <TableCell>
              Remove the stored team snapshot and membership state for this period.
            </TableCell>
            <TableCell>—</TableCell>
            <TableCell>
              <StyledButton
                data-testid="admin-assessments-delete-team-snapshots"
                onClick={handleDeleteSnapshots}
                disabled={
                  !selectedAssessmentPeriod ||
                  selectedAssessmentPeriod?.status !== ASSESSMENT_PERIOD_CLOSED ||
                  isDeletingSnapshots
                }
                label={isDeletingSnapshots ? 'Deleting...' : 'Delete Team Snapshot'}
                color="red"
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </PageSection>
  );
};

export default AssessmentAdmin;
